import React from 'react';

import { useMutation } from '@tanstack/react-query';
import { FolderPlusIcon } from '@vlabs/icons';
import { useModal } from '@vlabs/uikit';
import { Button } from '@vlabs/uikit/components/controls/button/Button';
import type { ButtonProps } from '@vlabs/uikit/components/controls/button/Button';
import type { WithDataTestid } from '@vlabs/uikit/types';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { updateLinker, getGetCardsQueryKey } from '@vlabs/api/artemy/client';
import { queryClient } from '@vlabs/api/queryClient';
import { buildTestid } from '@vlabs/monokit/shared/lib/utils';

import { AddCardsToListForm } from './forms';

const displayName = 'AttachCardsToListButton';
const testid = buildTestid(displayName);

export interface AttachCardsToListButtonProps extends
  WithDataTestid,
  ButtonProps {
  cardIds: string[];
}

export function AttachCardsToListButtonPoint3({
  cardIds,
  'data-testid': dataTestId = undefined,
  ...props
}: AttachCardsToListButtonProps): React.JSX.Element {
  const { t } = useTranslation();
  const modal = useModal({ size: 'small' });

  const moveCardsToListMutation = useMutation({
    mutationFn: async (attachListId: string) => {
      try {
        await updateLinker({
          action: 'attach',
          card_ids: cardIds,
          list_id: attachListId,
        });
      } catch (e) {
        toast.error(t('features:attach_cards_to_list.toast.error.attach'));
        throw e;
      }

      toast.success(t('features:attach_cards_to_list.toast.success'));
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: getGetCardsQueryKey(),
      });
      modal.close();
    },
  });

  return (
    <React.Fragment>
      {modal.wrap(<AddCardsToListForm
        onCancel={modal.close}
        onSubmit={moveCardsToListMutation.mutateAsync}
      />)}
      <Button
        icon={<FolderPlusIcon />}
        kind="primary"
        onClick={modal.open}
        size="small"
        variant="flat-inverted"
        {...testid(dataTestId)}
        {...props}
      >
        {t('features:attach_cards_to_list.buttons.attach')}
      </Button>
    </React.Fragment>
  );
}
